import { useState, useEffect, useCallback, useRef } from "react";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { ConnectionState } from "../sdk/types/transport";
import { normalizeProtobufTimestamp } from "../sdk/utils/time";
import { MultiFeedKlineData } from "../sdk/types/api";
import { KlineConfig, KlineData } from "../sdk/types/data";

/**
 * Hook for managing kline (OHLC candlestick) data with WebSocket real-time updates
 * Maintains a rolling buffer of kline data points (max 100 by default)
 */
export function useKline(config: KlineConfig) {
  const maxDataPoints = config.maxDataPoints || 100;
  const [klineData, setKlineData] = useState<KlineData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const isSubscribedRef = useRef<boolean>(false);
  const currentMarketIdRef = useRef<string | null>(null);
  const currentIntervalRef = useRef<string | null>(null);
  const currentSymbolRef = useRef<string | null>(null);

  /**
   * Handle kline update from WebSocket
   */
  const handleKlineUpdate = useCallback(
    (data: MultiFeedKlineData) => {
      if (!data) {
        console.warn(
          "[useKline] handleKlineUpdate received null/undefined data",
        );
        return;
      }

      try {
        // Convert protobuf timestamps to JavaScript timestamps (milliseconds)
        const openTime = normalizeProtobufTimestamp(data.open_time, Date.now());
        const closeTime = normalizeProtobufTimestamp(data.close_time, openTime);
        const timestamp = normalizeProtobufTimestamp(data.timestamp, openTime);

        // Convert satoshi strings to decimal numbers
        // Use utility function from math.ts for consistent conversion
        const kline: KlineData = {
          market_id: config.marketId,
          symbol: data.symbol,
          interval: data.interval || config.interval,
          open_time: openTime,
          close_time: closeTime,
          open: toNumberFromSatoshi(data.open),
          high: toNumberFromSatoshi(data.high),
          low: toNumberFromSatoshi(data.low),
          close: toNumberFromSatoshi(data.close),
          volume: toNumberFromSatoshi(data.volume),
          trade_count: data.trade_count || 0,
          timestamp: timestamp,
          raw: data, // Store raw data for modal display
        };

        setKlineData((prev) => {
          const updated = [...prev, kline];
          // Keep only the most recent maxDataPoints
          if (updated.length > maxDataPoints) {
            return updated.slice(-maxDataPoints);
          }
          return updated;
        });
      } catch (err) {
        console.error("[useKline] Error processing kline update:", err);
        setError(
          err instanceof Error ? err.message : "Failed to process kline data",
        );
      }
    },
    [config.marketId, config.interval, maxDataPoints],
  );

  // Subscribe to kline updates when client is available and ready
  useEffect(() => {
    const subscriptionClient = config.subscriptionClient;

    // Subscribe function that can be called when client becomes ready
    const subscribe = () => {
      // Check if ready for subscriptions (connected and authenticated if required)
      if (!subscriptionClient.isReadyForSubscriptions()) {
        if (
          subscriptionClient.isConnected() &&
          !subscriptionClient.isReadyForSubscriptions()
        ) {
          // Connected but not authenticated - wait a bit and retry
          const authState = subscriptionClient.getAuthState();
          if (authState === "pending") {
            console.debug(
              "[useKline] Waiting for authentication to complete...",
            );
            // Will retry on next state change
          } else if (authState === "failed") {
            console.error(
              "[useKline] Authentication failed - cannot subscribe to kline",
            );
            setError("Authentication failed - cannot subscribe to kline");
          }
        }
        return;
      }

      // Check if we need to re-subscribe (marketId, interval, or symbol changed)
      const needsResubscribe =
        !isSubscribedRef.current ||
        currentMarketIdRef.current !== config.marketId ||
        currentIntervalRef.current !== config.interval ||
        currentSymbolRef.current !== (config.symbol || "BTC/USDC");

      if (needsResubscribe) {
        // Unsubscribe from previous subscription if it exists
        if (isSubscribedRef.current) {
          try {
            subscriptionClient.unsubscribeMultiFeedSubChannel(
              "kline",
              handleKlineUpdate,
            );
          } catch (error) {
            console.warn(
              "[useKline] Error unsubscribing from previous kline subscription:",
              error,
            );
          }
        }

        try {
          // Subscribe via multi-feed kline sub-channel
          const symbol = config.symbol || "BTC/USDC"; // Default symbol
          subscriptionClient.subscribeMultiFeedSubChannel(
            "kline",
            config.marketId,
            handleKlineUpdate,
            {
              symbol,
              interval: config.interval,
            },
          );
          isSubscribedRef.current = true;
          currentMarketIdRef.current = config.marketId;
          currentIntervalRef.current = config.interval;
          currentSymbolRef.current = symbol;
          setLoading(false);
          console.debug("[useKline] Subscribed to kline:", {
            marketId: config.marketId,
            symbol,
            interval: config.interval,
          });
        } catch (error) {
          console.error("[useKline] Failed to subscribe to kline:", error);
          setError(
            error instanceof Error
              ? error.message
              : "Failed to subscribe to kline data",
          );
          isSubscribedRef.current = false;
          setLoading(false);
        }
      }
    };

    // Set initial connection state
    setIsConnected(subscriptionClient.isConnected());
    setLoading(false);

    // Try to subscribe immediately if client is ready
    if (subscriptionClient.isReadyForSubscriptions()) {
      subscribe();
    }

    // Monitor connection state changes and re-subscribe on reconnection
    // Use the public onStateChange method from SubscriptionClient
    const unsubscribeStateChange = subscriptionClient.onStateChange(
      (state: ConnectionState) => {
        console.debug("[useKline] WebSocket state changed to:", state);
        setIsConnected(state === ConnectionState.Connected);
        if (state === ConnectionState.Connected) {
          // Always try to subscribe on connection (subscribe function handles re-subscription logic)
          subscribe();
        } else if (
          state === ConnectionState.Disconnected ||
          state === ConnectionState.Reconnecting
        ) {
          // Don't reset isSubscribedRef here - let subscribe() handle re-subscription
          // This allows us to track if we need to re-subscribe with new params
        }
      },
    );

    return () => {
      // Unsubscribe from state changes
      if (unsubscribeStateChange) {
        unsubscribeStateChange();
      }

      // Unsubscribe from kline sub-channel
      if (subscriptionClient && isSubscribedRef.current) {
        try {
          subscriptionClient.unsubscribeMultiFeedSubChannel(
            "kline",
            handleKlineUpdate,
          );
        } catch (error) {
          console.warn("[useKline] Error unsubscribing during cleanup:", error);
        }
      }

      // Reset subscription state
      isSubscribedRef.current = false;
      currentMarketIdRef.current = null;
      currentIntervalRef.current = null;
      currentSymbolRef.current = null;
    };
  }, [
    config.subscriptionClient,
    config.marketId,
    config.interval,
    config.symbol,
    handleKlineUpdate,
  ]);

  return {
    klineData,
    loading,
    error,
    isConnected,
  };
}
